import React, { useMemo } from 'react';
import { FloorContainer } from "./AreaAndDeskUsageView";
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { Column, SpaceBetweenRow } from '@shared/ui/atoms/StructuralLayout';
import { HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { useDispatch, useSelector } from "react-redux";
import { selectActiveDay, selectActivePeriod } from "../../../../store/editMovements.duck";
import { Period } from '@features/whereabouts/models/movements.models';
import { bookSingleDayMovement } from "../../../../store/officeUsage.duck";
import { selectCurrentUser, selectIsInTeams } from "@shared/store/auth.duck";
import { UsersInOffice } from '@features/whereabouts/hooks/UsersInOffice';
import { ExpandableUserList } from "./ExpandableUserList";
import { WithOfficeCapacity } from '@features/desks/hooks/WithOfficeCapacity';
import { OfficeCapacityLabel } from '@shared/ui/molecules/OfficeCapacityLabel';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

export function SingleOfficeUsageView(props: Props) {
  const {office, isVisible} = props;
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const selectedDate = useSelector(selectActiveDay);
  const activePeriod = useSelector(selectActivePeriod);
  const isInTeams = useSelector(selectIsInTeams);

  const { capacity } = WithOfficeCapacity(office, selectedDate);
  const usersInOffice = UsersInOffice(office, selectedDate, capacity);

  const hasRoomToBook = useMemo(() => {
    // Has no capacity enabled
    if (!office.capacityEnabled) return true;

    if (capacity) {
      if (activePeriod === Period.AM) {
        return capacity.amUsed < capacity.capacity;
      } else if (activePeriod === Period.PM) {
        return capacity.pmUsed < capacity.capacity;
      } else {
        return Math.max(capacity.amUsed, capacity.pmUsed) < capacity.capacity;
      }
    }
    return false;
  }, [capacity, activePeriod, office])


  const bookSingleDay = (period: Period) => {
    if (selectedDate && hasRoomToBook && currentUser) {
      dispatch(bookSingleDayMovement({
        date: selectedDate,
        office: office,
        userId: currentUser.id,
        period: period
      }));
    }
  }

  return (
    <FloorContainer>
      <SpaceBetweenRow>
        <Column>
          <HeadlineSmall>{office.label}</HeadlineSmall>
          <OfficeCapacityLabel capacity={capacity} />
        </Column>

        <div>

          <div style={{alignItems: 'center', display: 'flex'}}>
            {office.floorPlanEnabled && !isInTeams && <OutlineButton text={'button.view'}
                                                                       size={ButtonSize.Small}
                                                                       style={{marginRight: 8}}
                                                                       click={() => window.location.href = `/whereabouts/company/all?office=${office.id}&view=floor`} />}

            {!office.deskBookingEnabled && !isInTeams && <OutlineButton size={ButtonSize.Small}
                                                                          disabled={!office.active || !hasRoomToBook}
                                                                          click={() => bookSingleDay(activePeriod)}
                                                                          text="button.book"
                                                                          dataTest="book-into-office-button" />}

          </div>
        </div>
      </SpaceBetweenRow>

      <ExpandableUserList office={office}
                          isLoading={usersInOffice.loading}
                          users={usersInOffice.users}
                          isVisible={isVisible} />
    </FloorContainer>
  )
}

interface Props {
  office: OfficeEntity;
  isVisible: boolean;
}
